import React from 'react';

import { FetchStatus } from '@neptune/shared/common-domain';
import { plural } from '@neptune/shared/common-util';
import { QueryFilterSearch } from '@neptune/shared/search-domain';
import { bemBlock, Layout, Modal, Text } from '@neptune/shared/venus-ui';

import LoadingIndicator from 'components/loading-indicator/LoadingIndicator';
import { KnownAttributes } from 'domain/experiment/attribute';

import { AddTagWithAutocompletion } from './AddTagWithAutocompletion';
import { TagRow } from './TagRow';

import './ManageTagsModal.less';

const block = bemBlock('manage-tags-modal');

export type ManageTagsModalSection = {
  attributeName: string;
  colorGetter: (tag: string) => string;
  error?: string;
  dataSource: QueryFilterSearch<string>;
  tagsCount: Record<string, number>;
  tagsToAdd: string[];
  tagsToRemove: string[];
  addTag: (tag: string) => void;
  resetTag: (tag: string) => void;
  onToggle: (tag: string, isChecked: boolean) => void;
};

type ManageTagsModalProps = {
  fetchStatus: FetchStatus;
  entries: any[];
  isSubmitting?: boolean;
  sections: ManageTagsModalSection[];
  onSubmit: () => void;
  onClose: () => void;
};

export const ManageTagsModal: React.FC<ManageTagsModalProps> = ({
  fetchStatus,
  entries,
  isSubmitting = false,
  sections,
  onClose,
  onSubmit,
}) => {
  const totalChanges = sections.reduce((total, section) => {
    const newTags = getNewTags(section.tagsToAdd, section.tagsCount);

    return total + newTags.length + section.tagsToAdd.length + section.tagsToRemove.length;
  }, 0);

  return (
    <>
      <LoadingIndicator className={block('loading')} fetchStatus={fetchStatus} centered />
      <Modal
        isOpen={entries.length > 0}
        onClose={isSubmitting ? undefined : onClose}
        shouldCloseOnEsc={!isSubmitting}
        data={{
          role: 'manage-tags-modal',
        }}
      >
        <Modal.Header
          title={getModalTitle(sections)}
          onClose={isSubmitting ? undefined : onClose}
        />
        <Modal.Body className={block('body')}>
          <Layout.Column className={block('wrapper')} spacedChildren="lg">
            {sections.map((section, index) => (
              <React.Fragment key={section.attributeName}>
                {index > 0 && <Layout.Separator />}
                <ManageTagsSection
                  entries={entries}
                  isSubmitting={isSubmitting}
                  section={section}
                />
              </React.Fragment>
            ))}
          </Layout.Column>
        </Modal.Body>
        <Modal.Footer
          primaryButtonLabel="Apply"
          primaryButtonRole="confirm-button"
          primaryButtonDisabled={totalChanges === 0 || isSubmitting}
          secondaryButtonDisabled={isSubmitting}
          onPrimaryButtonClick={onSubmit}
          onSecondaryButtonClick={onClose}
        />
      </Modal>
    </>
  );
};

const ManageTagsSection: React.FC<{
  entries: any[];
  isSubmitting: boolean;
  section: ManageTagsModalSection;
}> = ({ entries, isSubmitting, section }) => {
  const {
    attributeName,
    colorGetter,
    dataSource,
    tagsCount,
    tagsToAdd,
    tagsToRemove,
    resetTag,
    addTag,
    onToggle,
    error,
  } = section;
  const newTags = getNewTags(tagsToAdd, tagsCount);
  const tagsCountEntries = Object.entries(tagsCount);

  return (
    <Layout.Column className={block('section')} spacedChildren="md">
      <Layout.Column spacedChildren="xxs">
        <Text component="div" size="md" fontWeight="semibold">
          {getAttributeLabelTitle(attributeName)}
        </Text>
        <Text component="div" size="sm" color="text-secondary">
          {getAttributeDescription(attributeName)}
        </Text>
      </Layout.Column>
      {(newTags.length > 0 || tagsCountEntries.length > 0) && (
        <Layout.Column overflow="auto" spacedChildren="md">
          <React.Fragment>
            {newTags.length > 0 && (
              <React.Fragment>
                <TagActionStatus
                  count={newTags.length}
                  text={`new ${getAttributeLabel(attributeName, newTags.length)} added`}
                />
                {newTags.map((tag) => {
                  return (
                    <TagRow
                      key={tag}
                      applyCount={entries.length}
                      allCount={entries.length}
                      actionText="Remove"
                      initialCount={0}
                      tag={tag}
                      disabled={isSubmitting}
                      onAction={resetTag}
                      color={
                        attributeName === KnownAttributes.GroupTags ? colorGetter(tag) : undefined
                      }
                    />
                  );
                })}
                {tagsCountEntries.length > 0 && <Layout.Separator />}
              </React.Fragment>
            )}
            {tagsCountEntries.length > 0 && (
              <React.Fragment>
                <TagActionStatus
                  count={tagsCountEntries.length}
                  text={`${getAttributeLabel(
                    attributeName,
                    tagsCountEntries.length,
                  )} used in selected runs`}
                />
                {tagsCountEntries.map(([tag, count]) => {
                  let countAfterApply = count;

                  if (tagsToAdd.includes(tag)) {
                    countAfterApply = entries.length;
                  }

                  if (tagsToRemove.includes(tag)) {
                    countAfterApply = 0;
                  }

                  return (
                    <TagRow
                      key={tag}
                      allCount={entries.length}
                      applyCount={countAfterApply}
                      initialCount={tagsCount[tag]}
                      actionText="Reset"
                      tag={tag}
                      disabled={isSubmitting}
                      onChange={onToggle}
                      onAction={resetTag}
                      color={
                        attributeName === KnownAttributes.GroupTags ? colorGetter(tag) : undefined
                      }
                    />
                  );
                })}
              </React.Fragment>
            )}
          </React.Fragment>
        </Layout.Column>
      )}

      <Layout.Column span="auto" spacedChildren="md" data-role="add-tag-section">
        <Text component="div" size="sm" fontWeight="normal">
          Add new {getAttributeLabel(attributeName, 1)} to all selected runs
        </Text>
        <AddTagWithAutocompletion
          disabled={isSubmitting}
          dataSource={dataSource}
          onTagAdd={addTag}
        />
        {error && <div className={block('error')}> {error} </div>}
      </Layout.Column>
    </Layout.Column>
  );
};

const TagActionStatus: React.FC<{
  count: number;
  text: string;
}> = ({ count, text }) => {
  return (
    <Text component="div" size="sm" fontWeight="normal">
      <Text fontWeight="semibold">{count}</Text> {text}
    </Text>
  );
};

function getNewTags(tagsToAdd: string[], tagsCount: Record<string, number>) {
  return tagsToAdd.filter((tag) => !(tagsCount[tag] > 0)).reverse();
}

function getModalTitle(sections: ManageTagsModalSection[]) {
  return sections.length > 1
    ? 'Manage tags'
    : `Manage ${getAttributeLabel(sections[0]?.attributeName, 2)}`;
}

function getAttributeLabelTitle(attributeName: string) {
  switch (attributeName) {
    case KnownAttributes.Tags:
      return 'Tags';
    case KnownAttributes.GroupTags:
      return 'Group tags';
    default:
      return attributeName;
  }
}

function getAttributeLabel(attributeName: string | undefined, count: number) {
  switch (attributeName) {
    case KnownAttributes.Tags:
      return plural(count, { one: 'tag', other: 'tags' });
    case KnownAttributes.GroupTags:
      return plural(count, { one: 'group tag', other: 'group tags' });
    default:
      return attributeName ?? 'tags';
  }
}

function getAttributeDescription(attributeName: string) {
  switch (attributeName) {
    case KnownAttributes.Tags:
      return 'Use tags for quick run identification and filtering.';
    case KnownAttributes.GroupTags:
      return 'Use group tags to label run categories; they are used by default when grouping runs.';
    default:
      return '';
  }
}
